import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Link2, Unlink } from "lucide-react";
import { StatusPill, LocCell, RelocateHint } from "@/components/network/StatusPill";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { KodeHistory } from "@/components/network/KodeHistory";
import { ServerInfo } from "@/components/network/ServerInfo";
import { SplitterInputDialog, SplitterOutputDialog, SplitterEndDialog } from "@/components/network/SplitterDialogs";

const fmt = (v) => (v ? new Date(v).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "short", timeStyle: "short" }) : "-");

const KL = { pon: "PON", odc: "ODC", odp: "ODP", splitter: "Splitter" };
const srcText = (r) => (r.source_kind === "splitter" ? `Splitter ${r.source?.kode || r.source_kode || "?"} output ${r.source_output_port}`
  : r.source_kind && r.source_kind !== "pon" ? `${KL[r.source_kind]} ${r.source?.kode || r.source_kode || "?"}` : `${r.olt?.kode || "?"} PON ${r.port?.nama_port || "?"}`);
const tgtText = (o) => `${KL[o.target_kind || "odc"]} ${(o.target || o.odc)?.kode || o.target_kode || "?"}`;

function History({ rows, label }) {
  if (!rows.length) return null;
  return (
    <details className="text-xs" data-testid={`splitter-history-${label}`}>
      <summary className="cursor-pointer font-semibold text-[#6B7280]">Riwayat {label} ({rows.length})</summary>
      <ul className="mt-1 space-y-1">{rows.map((r) => (
        <li key={r.id} className="rounded bg-[#F8F9FA] p-1.5">
          {label === "output" ? `Output ${r.output_port} → ${tgtText(r)}` : `Dari ${srcText(r)}`}
          <span className="text-[#6B7280]"> · {fmt(r.started_at)} s/d {fmt(r.ended_at)} · {r.end_reason}</span></li>))}</ul>
    </details>
  );
}

export function SplitterDetail({ splitterId, canManage, me, wilayahName, onChanged, onDeleted, onSelect }) {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState({});
  const load = useCallback(() => api.get(`/network/splitters/${splitterId}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat splitter")), [splitterId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="p-3 text-xs">Memuat…</p>;
  const done = () => { setDlg({}); load(); onChanged?.(); };
  const usable = canManage && d.status === "AKTIF";
  const slots = Array.from({ length: d.jumlah_output }, (_, i) => i + 1);
  const go = (kind, id) => onSelect && id && onSelect({ kind, id });
  return (
    <div className="space-y-3 text-sm" data-testid="splitter-detail">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-mono-tab text-lg font-bold">{d.kode}</div>
          <div>{d.nama}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#6B7280]"><StatusPill status={d.status} />
            <span data-testid="splitter-detail-rasio">Rasio {d.rasio} · {d.output_terpakai}/{d.jumlah_output} output terpakai</span>
            {(d.in_server || !d.has_coords) && <LocCell row={d} testid="splitter-detail-nocoord" />}</div>
        </div>
        <div className="flex gap-2">
          {d.has_coords && !d.in_server && <ArahkanButton lat={d.latitude} lng={d.longitude} testid="splitter-nav-btn" />}
          <DeleteInfraButton kind="splitter" item={d} me={me} onDeleted={onDeleted} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
        <div>Wilayah: <b>{wilayahName?.(d.wilayah_id)}</b></div>
        {d.lokasi_fisik && <div>Posisi fisik: <b>{d.lokasi_fisik}</b></div>}
        {d.alamat && <div className="sm:col-span-2">Alamat: {d.alamat}</div>}
        {d.catatan && <div className="sm:col-span-2">Catatan: {d.catatan}</div>}
      </div>
      <section className="rounded border border-[#E5E7EB] p-2" data-testid="splitter-input">
        <div className="mb-1 text-xs font-bold uppercase text-[#6B7280]">Input</div>
        {d.input ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button type="button" className="font-semibold text-[#0055FF] disabled:text-inherit" disabled={!onSelect}
                    onClick={() => (d.input.source_kind === "pon" ? go("olt", d.input.olt?.id) : go(d.input.source_kind, d.input.source_id))}
                    data-testid="splitter-input-label">{d.input.source_kind === "pon" ? `${d.input.olt?.kode} · PON ${d.input.port?.nama_port}` : srcText(d.input)}</button>
            {d.input.olt?.status && <span className="flex items-center gap-1 text-xs text-[#6B7280]" data-testid="splitter-input-olt-status">Status OLT: <StatusPill status={d.input.olt.status} /></span>}
            {canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ end: { url: `/network/splitter-${d.input.rel === "output" ? "outputs" : "inputs"}/${d.input.id}/disconnect`, label: `input ${d.kode}` } })}
                                  data-testid="splitter-input-disconnect"><Unlink className="mr-1 h-3.5 w-3.5" />Putus</Button>}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-xs text-[#B45309]">Belum terhubung ke sumber (PON/ODC/ODP/splitter)</span>
            {usable && <Button size="sm" variant="outline" onClick={() => setDlg({ input: d })} data-testid="splitter-input-connect"><Link2 className="mr-1 h-3.5 w-3.5" />Hubungkan</Button>}</div>
        )}
      </section>
      <section className="rounded border border-[#E5E7EB]" data-testid="splitter-outputs">
        <div className="border-b p-2 text-xs font-bold uppercase text-[#6B7280]">Output</div>
        <ul className="divide-y">{slots.map((n) => {
          const o = d.outputs.find((x) => x.output_port === n);
          return (
            <li key={n} className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5" data-testid={`splitter-out-${n}`}>
              <span className="text-xs"><b>Output {n}</b> → {o
                ? <button type="button" className="font-semibold text-[#0055FF] disabled:text-inherit" disabled={!onSelect} onClick={() => go(o.target_kind || "odc", o.target_id || o.odc_id)}
                          data-testid={`splitter-out-odc-${n}`}>{tgtText(o)} <span className="font-normal text-[#6B7280]">{(o.target || o.odc)?.nama}</span></button>
                : <span className="text-[#6B7280]">Kosong</span>}</span>
              {o && canManage && <Button size="sm" variant="ghost" onClick={() => setDlg({ end: { url: `/network/splitter-outputs/${o.id}/disconnect`, label: `output ${n} → ${tgtText(o)}` } })}
                                         data-testid={`splitter-out-disconnect-${n}`}><Unlink className="mr-1 h-3.5 w-3.5" />Putus</Button>}
              {!o && usable && <Button size="sm" variant="ghost" onClick={() => setDlg({ output: { sp: d, port: n } })} data-testid={`splitter-out-connect-${n}`}>
                <Link2 className="mr-1 h-3.5 w-3.5" />Hubungkan</Button>}
            </li>
          );
        })}</ul>
      </section>
      <History rows={d.input_history} label="input" />
      <History rows={d.output_history} label="output" />
      <RelocateHint show={!d.in_server && !d.has_coords && (!!d.input || d.output_terpakai > 0)} testid="splitter-relocate-hint" />
      <ServerInfo kind="splitter" id={d.id} onSelect={onSelect} />
      <KodeHistory kind="splitter" id={d.id} kode={d.kode} />
      <SplitterInputDialog sp={dlg.input} onClose={() => setDlg({})} onSaved={done} />
      <SplitterOutputDialog target={dlg.output} onClose={() => setDlg({})} onSaved={done} />
      <SplitterEndDialog target={dlg.end} onClose={() => setDlg({})} onSaved={done} />
    </div>
  );
}
